import { ChevronRight, Search as SearchIcon } from '@mui/icons-material';
import HelpIcon from '@mui/icons-material/Help';
import {
    Box,
    Button,
    InputAdornment,
    Menu,
    MenuItem,
    Stack,
    type SxProps,
    TextField,
    type Theme,
    Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { trim } from 'lodash';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import PageContainer from '/@/components/bayer/page-container';
import Icon from '/@/components/icon';
import useUnitProvider from '/@/core/context/unit';
import { useAllCatalogProducts } from '/@/hooks/catalog';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { _ } from '/@/locale';
import { getCatalogBrand } from '/@/store/catalog/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { reverse } from '/@/utils/router';
import CardSection from '../../components/bayer/card-section';
import HeroHeader from '../../components/bayer/hero-header';
import LatestSearchedProducts from './components/latest-searched-products';
import { parseProductForProductCard } from './components/product-card';
import ProductComparator from './components/product-comparator';
import { useProductComparator } from './hooks/product-comparator';
import useProductsFilterOptions from './hooks/product-filter-options';
import { PRODUCT_EXPLORATION_TOUR_IDS } from './tour/product-exploration-tour-ids';
import { useProductExplorationTour } from './tour/useProductExplorationTour';

const ProductExploration = () => {
    // Core
    const navigate = useNavigate();
    const units = useUnitProvider();
    const { startTourFromLanding, tourPathsReady } = useProductExplorationTour();

    // Product Comparator
    const {
        selectProduct,
        isSelected,
        getTypeOfIds,
        selectedProducts,
        selectedType,
        setSelectedType,
        resetAndSelectMultipleProducts,
        resetSelectedProducts,
    } = useProductComparator();

    // Search Products
    const [search, setSearch] = useState('');
    const [searchAnchorEl, setSearchAnchorEl] = useState<null | HTMLElement>(null);
    const debouncedSearch = useDebounce(search, 250);
    const dekalbArCornProductsQuery = useAllCatalogProducts({
        brand: 'dekalb',
        crop: 'corn',
        country: 'AR',
        page: 1,
        pageSize: 10000,
    });
    const cpBayerARProductsQuery = useAllCatalogProducts({
        brand: 'bayer',
        crop: 'crop_protection',
        country: 'AR',
        page: 1,
        pageSize: 10000,
    });
    const allProducts = useMemo(() => {
        const dekalbArCornProducts = parseProductForProductCard(
            (dekalbArCornProductsQuery.data?.results || [])
                .filter((p) => Boolean(_(p.catalogZone)))
                .map((p) => ({ ...p, catalogType: 'corn', slugLabel: p.brand.name.toLowerCase() })),
            units
        );
        const cpBayerARProducts = parseProductForProductCard(
            (cpBayerARProductsQuery.data?.results || []).map((p) => ({
                ...p,
                catalogType: 'crop_protection',
                slugLabel: p.brand.toLowerCase(),
            })),
            units
        );

        return [...dekalbArCornProducts, ...cpBayerARProducts];
    }, [dekalbArCornProductsQuery, cpBayerARProductsQuery]);
    const filterOptions = useProductsFilterOptions(allProducts);
    const options = useMemo<
        [
            string,
            string,
            {
                text: string;
                sx: SxProps<Theme>;
            }[],
        ][]
    >(() => {
        const productsOptions = allProducts.map((p) => [
            p.name,
            reverse('productExploration.detail', {
                params: {
                    type: p.catalogType,
                    slug: p.slugLabel,
                    productId: p.id,
                },
            }),
            p.chips,
        ]);
        const parseFilterOptions = (options: [string, string][], path: (id: string) => string) =>
            options.map(([id, value]) => [value, path(id)]);
        const parsedFilterOptions = [
            ...parseFilterOptions(filterOptions.cropOptionsArray, (id) =>
                reverse('productExploration.list', {
                    params: { type: 'crop_protection', slug: 'bayer' },
                    qs: { crop: id },
                })
            ),
            ...parseFilterOptions(filterOptions.typeOptionsArray, (id) =>
                reverse('productExploration.list', {
                    params: { type: 'crop_protection', slug: 'bayer' },
                    qs: { type: id },
                })
            ),
            ...parseFilterOptions(filterOptions.technologyOptionsArray, (id) =>
                reverse('productExploration.list', {
                    params: { type: 'corn', slug: 'dekalb' },
                    qs: { technology: id },
                })
            ),
            ...parseFilterOptions(filterOptions.cycleOptionsArray, (id) =>
                reverse('productExploration.list', {
                    params: { type: 'corn', slug: 'dekalb' },
                    qs: { cycle: id },
                })
            ),
            ...parseFilterOptions(filterOptions.zoneOptionsArray, (id) =>
                reverse('productExploration.list', {
                    params: { type: 'corn', slug: 'dekalb' },
                    qs: { zone: id },
                })
            ),
        ];

        return [...parsedFilterOptions, ...productsOptions];
    }, [filterOptions, allProducts]);
    const filteredOptions = useMemo(
        () => options.filter(([value]) => value.toLowerCase().includes(trim(debouncedSearch.toLowerCase()))),
        [debouncedSearch, options]
    );

    const country = useSelector(getSessionCountry);
    const cropProtectionBrand = useSelector((state) => getCatalogBrand(state, 'crop_protection'));
    const cropProtectionProductsQuery = useAllCatalogProducts({
        brand: cropProtectionBrand?.name,
        crop: 'crop_protection',
        country: country!.code!,
        cropProtectionCrop: undefined,
        cropProtectionProductType: undefined,
    });
    const cropProtectionProductsEnabled = cropProtectionBrand && cropProtectionProductsQuery.data?.results?.length > 0;

    return (
        <PageContainer
            heroHeader={
                <HeroHeader
                    titleIcon={
                        tourPathsReady && (
                            <Box
                                id={PRODUCT_EXPLORATION_TOUR_IDS.HELP_LANDING}
                                component='span'
                                sx={{ display: 'inline-flex', cursor: 'pointer' }}
                            >
                                <HelpIcon
                                    color='white'
                                    sx={{ width: 24, height: 24 }}
                                    onClick={() => tourPathsReady && startTourFromLanding()}
                                />
                            </Box>
                        )
                    }
                    background={'cornPlants'}
                    title={_('Productos')}
                    body={_(
                        'Explorá el catálogo de híbridos y productos de protección de cultivos de Bayer con información técnica clave, sus beneficios, usos recomendados y el momento ideal de aplicación para cada situación.'
                    )}
                />
            }
        >
            <Stack id={PRODUCT_EXPLORATION_TOUR_IDS.LANDING_ROOT} spacing={5}>
                <Stack spacing={2}>
                    <Typography color='primary' variant='h6'>
                        {_('Explora productos')}
                    </Typography>
                    <Box
                        id={PRODUCT_EXPLORATION_TOUR_IDS.SEARCH}
                        sx={{
                            width: '100%',
                            display: 'block',
                            '& .MuiTextField-root': { margin: 0 },
                            '& .MuiFormControl-root': { margin: 0, verticalAlign: 'top' },
                        }}
                    >
                        <TextField
                            fullWidth
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setSearchAnchorEl(e.currentTarget);
                            }}
                            placeholder={_('Busca por nombre o tipo de producto')}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <Menu
                        open={search.length > 0 && filteredOptions.length > 0 && Boolean(searchAnchorEl)}
                        onClose={() => setSearchAnchorEl(null)}
                        anchorEl={searchAnchorEl}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        slotProps={{ paper: { sx: { width: '100%' } } }}
                        disableEnforceFocus
                        disableAutoFocus
                        autoFocus={false}
                    >
                        {filteredOptions.slice(0, 5).map(([label, path, chips]) => (
                            <MenuItem key={label} onClick={() => navigate(path)}>
                                <Stack spacing={1} direction='row' alignItems='center'>
                                    <span>{label}</span>
                                    {(chips || []).map((chip) => {
                                        return chip;
                                    })}
                                </Stack>
                            </MenuItem>
                        ))}
                    </Menu>
                </Stack>
                <Grid2 id={PRODUCT_EXPLORATION_TOUR_IDS.CTA_GRID} container>
                    <Grid2 item xs={12} sm={6} pr={{ sm: 1.5 }} mb={{ xs: 1.5, sm: 0 }}>
                        <CardSection
                            color='gray'
                            title={_('Buscar un híbrido para recomendar')}
                            description={_(
                                'Accedé a semillas Dekalb con información técnica para ayudarte a elegir la mejor opción según el lote del productor.'
                            )}
                            button={
                                <Button
                                    variant='contained'
                                    color='secondary'
                                    endIcon={<ChevronRight />}
                                    onClick={() =>
                                        navigate(
                                            reverse('productExploration.list', {
                                                params: {
                                                    type: 'corn',
                                                    slug: 'dekalb',
                                                },
                                            })
                                        )
                                    }
                                >
                                    {_('Explorar híbridos')}
                                </Button>
                            }
                            icon={<Icon type='cropProtection' style={{ width: '40px', height: '40px' }} />}
                            size='lg'
                        />
                    </Grid2>
                    {cropProtectionProductsEnabled && (
                        <Grid2 item xs={12} sm={6} pl={{ sm: 1.5 }} my={{ xs: 1.5, sm: 0 }}>
                            <CardSection
                                color='gray'
                                title={_('Elegir productos para proteger cultivos')}
                                description={_(
                                    'Encontrá herbicidas, insecticidas y fungicidas para sugerir al productor según el momento del cultivo y el problema a tratar.'
                                )}
                                button={
                                    <Button
                                        variant='contained'
                                        color='secondary'
                                        endIcon={<ChevronRight />}
                                        disabled={!cropProtectionProductsEnabled}
                                        onClick={() =>
                                            navigate(
                                                reverse('productExploration.list', {
                                                    params: {
                                                        type: 'crop_protection',
                                                        slug: 'bayer',
                                                    },
                                                })
                                            )
                                        }
                                    >
                                        {_('Buscar fitosanitarios')}
                                    </Button>
                                }
                                icon={<Icon type='cornNew' style={{ width: '40px', height: '40px' }} />}
                                size='lg'
                            />
                        </Grid2>
                    )}
                </Grid2>
                {/* <LatestRecommendedProducts
                    getTypeOfIds={getTypeOfIds}
                    setSelectedType={setSelectedType}
                    selectProduct={selectProduct}
                    isSelected={isSelected}
                    selectedType={selectedType}
                    selectedProducts={selectedProducts}
                /> */}
                <LatestSearchedProducts
                    getTypeOfIds={getTypeOfIds}
                    setSelectedType={setSelectedType}
                    selectProduct={selectProduct}
                    isSelected={isSelected}
                    selectedType={selectedType}
                    selectedProducts={selectedProducts}
                    allProducts={allProducts}
                />

                <ProductComparator
                    selectedProducts={selectedProducts}
                    selectProduct={selectProduct}
                    selectedType={selectedType}
                    resetSelectedProducts={resetSelectedProducts}
                    resetAndSelectMultipleProducts={resetAndSelectMultipleProducts}
                />
            </Stack>
        </PageContainer>
    );
};

export default ProductExploration;
